/**
 * Administration → Implementation Approvals.
 *
 * Who signs off implementation plans and date changes, and whether either
 * gate is enforced at all. Super-admin only to change: this decides who can
 * approve everyone else's schedules, so it must not be something a manager
 * can quietly add themselves to.
 *
 * Super admins can always approve regardless of this list. That is a
 * deliberate safety net — without it, an approver leaving the company would
 * strand every pending plan with nobody able to clear it.
 */

import { useCallback, useEffect, useState } from "react";
import { Select } from "antd";
import { RefreshCw, ShieldCheck, UserCheck, X } from "lucide-react";

import useApiMethods from "../../lib/useApiMethods";
import ToastMsg from "../../components/common/ToastMsg";
import { ContainerCard } from "../../components/ui/cards";
import { Button } from "../../components/ui/button";
import {
  IMPLEMENTATION_APPROVAL_CONFIG_URL, USERS_SIMPLE_URL,
} from "../../constants/api";

export default function ImplementationApprovals() {
  const { fetch, update } = useApiMethods();

  const [cfg, setCfg]         = useState(null);
  const [users, setUsers]     = useState([]);
  const [ids, setIds]         = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving]   = useState(false);

  const load = useCallback(() => {
    fetch(IMPLEMENTATION_APPROVAL_CONFIG_URL, {}, setLoading).then(res => {
      if (res?.status === "success") {
        setCfg(res.data);
        setIds((res.data.approvers || []).map(a => a.id));
      } else ToastMsg(res?.message || "Could not load the configuration", "error");
    });
    fetch(USERS_SIMPLE_URL).then(res => {
      const list = res?.data || res?.results || [];
      setUsers(list.map(u => ({
        value: u.id,
        label: [u.login_fname, u.login_lname].filter(Boolean).join(" ") || u.user_name,
      })));
    }).catch(() => {});
  }, []);   // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const save = async (patch = {}) => {
    setSaving(true);
    const res = await update(IMPLEMENTATION_APPROVAL_CONFIG_URL, {
      approver_ids: ids,
      require_plan_approval: cfg?.require_plan_approval,
      require_due_change_approval: cfg?.require_due_change_approval,
      ...patch,
    });
    setSaving(false);
    if (res?.status === "success") {
      setCfg(res.data);
      setIds((res.data.approvers || []).map(a => a.id));
      ToastMsg("Saved", "success");
    } else ToastMsg(res?.message || "Failed to save", "error");
  };

  const readOnly = cfg && !cfg.can_edit;

  return (
    <ContainerCard>
      <div className="space-y-5 p-1">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="flex items-center gap-2 text-base font-semibold text-gray-900 dark:text-white">
              <ShieldCheck className="h-4 w-4 text-indigo-500" />
              Implementation Approvals
            </h2>
            <p className="mt-1 max-w-2xl text-xs leading-relaxed text-gray-500 dark:text-gray-400">
              Who signs off implementation plans and changes to agreed dates. A plan
              built from a template cannot be started, held or completed until an
              approver has passed it.
            </p>
          </div>
          <Button size="sm" variant="outline" onClick={load} disabled={loading}>
            <RefreshCw className={`mr-1 h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>

        {readOnly && (
          <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800
                        dark:bg-amber-500/10 dark:text-amber-300">
            You can see this configuration but only a super admin can change it.
          </p>
        )}

        {/* ── approvers ────────────────────────────────────────────────── */}
        <section className="rounded-lg border border-gray-200 p-4 dark:border-gray-700">
          <h3 className="flex items-center gap-2 text-sm font-medium text-gray-800 dark:text-gray-100">
            <UserCheck className="h-3.5 w-3.5 text-gray-400" /> Approvers
          </h3>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            These people see the Approve / Reject buttons and are notified when a plan
            or a date change needs a decision.
          </p>

          <div className="mt-3 flex flex-wrap items-center gap-2">
            {(cfg?.approvers || []).length === 0 && (
              <span className="text-xs text-gray-400">
                Nobody is set — only super admins can approve.
              </span>
            )}
            {(cfg?.approvers || []).map(a => (
              <span key={a.id}
                className="inline-flex items-center gap-1.5 rounded-full bg-indigo-50 px-2.5 py-1
                           text-xs font-medium text-indigo-700 ring-1 ring-inset ring-indigo-200
                           dark:bg-indigo-500/10 dark:text-indigo-300 dark:ring-indigo-500/30">
                {a.name || `User ${a.id}`}
                {!readOnly && (
                  <button title="Remove"
                    onClick={() => {
                      const next = ids.filter(i => i !== a.id);
                      setIds(next);
                      save({ approver_ids: next });
                    }}>
                    <X className="h-3 w-3 opacity-60 hover:opacity-100" />
                  </button>
                )}
              </span>
            ))}
          </div>

          {!readOnly && (
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <Select showSearch allowClear className="w-64" placeholder="Add an approver"
                optionFilterProp="label"
                options={users.filter(u => !ids.includes(u.value))}
                value={undefined}
                onChange={v => {
                  if (!v) return;
                  const next = [...new Set([...ids, v])];
                  setIds(next);
                  save({ approver_ids: next });
                }} />
              {saving && <span className="text-xs text-gray-400">Saving…</span>}
            </div>
          )}
        </section>

        {/* ── the two gates ────────────────────────────────────────────── */}
        <section className="rounded-lg border border-gray-200 p-4 dark:border-gray-700">
          <h3 className="text-sm font-medium text-gray-800 dark:text-gray-100">Gates</h3>
          <div className="mt-3 space-y-3">
            <Toggle
              disabled={readOnly || saving}
              checked={!!cfg?.require_plan_approval}
              onChange={v => { setCfg(c => ({ ...c, require_plan_approval: v }));
                               save({ require_plan_approval: v }); }}
              label="Plans need approval before work starts"
              hint="With this off, a plan can be worked immediately and approval becomes
                    sign-off for the record only. Clients added before this feature are
                    never gated either way."
            />
            <Toggle
              disabled={readOnly || saving}
              checked={!!cfg?.require_due_change_approval}
              onChange={v => { setCfg(c => ({ ...c, require_due_change_approval: v }));
                               save({ require_due_change_approval: v }); }}
              label="Changing an agreed due date needs approval"
              hint="Applies only once a plan is approved. A client-side hold still moves the
                    deadline automatically — that is a rule, not a decision, so it is never
                    sent for approval."
            />
          </div>
        </section>

        {cfg?.i_am_approver && (
          <p className="text-xs text-emerald-600 dark:text-emerald-400">
            You are an approver, so plans and date changes will come to you.
          </p>
        )}
      </div>
    </ContainerCard>
  );
}

function Toggle({ checked, onChange, label, hint, disabled }) {
  return (
    <label className={`flex items-start gap-3 ${disabled ? "opacity-60" : "cursor-pointer"}`}>
      <input type="checkbox" className="mt-0.5" checked={checked} disabled={disabled}
        onChange={e => onChange(e.target.checked)} />
      <span>
        <span className="block text-xs font-medium text-gray-800 dark:text-gray-100">{label}</span>
        <span className="mt-0.5 block max-w-xl text-[11px] leading-relaxed text-gray-500 dark:text-gray-400">
          {hint}
        </span>
      </span>
    </label>
  );
}
